import { BasedObservedViewModel } from "app/based/basedObservedViewModel/BasedObservedViewModel";
import { ApiDocumentInjection } from "features/apiDocumentFeature/di/ApiDocumentInjection";
import { ApiDocumentRequestEntity } from "features/apiDocumentFeature/domain/entities/ApiDocumentRequestEntity";
import { ApiDocumentUseCase } from "features/apiDocumentFeature/domain/useCases/ApiDocumentUseCase";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { strings } from "utils/localizedStrings";
import { IApiDocumentObservedState } from "../interfaces/IApiDocument";

// ─── Input ────────────────────────────────────────────────────────────────────

class ApiDocumentInput implements Disposable {
  public fetchDoc = new BehaviorSubject<ApiDocumentRequestEntity>({});
  public fetchServices = new BehaviorSubject<any>({});

  [Symbol.dispose](): void {
    this.fetchDoc.complete();
    this.fetchServices.complete();
  }
}

// ─── Output ───────────────────────────────────────────────────────────────────

class ApiDocumentOutput implements Disposable {
  public onError = new BehaviorSubject<string>("");

  [Symbol.dispose](): void {
    this.onError.complete();
  }
}

// ─── ViewModel ────────────────────────────────────────────────────────────────

@injectable()
export class ApiDocumentViewModel extends BasedObservedViewModel<
  ApiDocumentInput,
  ApiDocumentOutput,
  IApiDocumentObservedState
> {
  private useCase = ApiDocumentInjection.getUseCase();

  protected override createObservedState(): IApiDocumentObservedState {
    return {
      loading: false,
      list: [],
      serviceOptions: [],
    };
  }

  constructor() {
    super(new ApiDocumentInput(), new ApiDocumentOutput());
  }

  protected override transform(): void {
    // Fetch api documents
    this.skipFirstInitial(this.input.fetchDoc, async (params) => {
      try {
        this.setObservedState({ loading: true, list: [] });
        const res = await this.useCase.getApiDocuments(params);
        if (res?.data) {
          this.setObservedState({ list: res.data });
        }
      } catch (error: any) {
        console.error(error);
        this.setObservedState({ list: [] });
        this.output.onError.next(
          error?.message || strings.GlobalMessage.Error
        );
      } finally {
        this.setObservedState({ loading: false });
      }
    });

    // Fetch services
    this.skipFirstInitial(this.input.fetchServices, async () => {
      try {
        const res = await this.useCase.getServiceList();
        if (res) {
          this.setObservedState({ serviceOptions: res });
        }
      } catch (error: any) {
        console.error(error);
        this.output.onError.next(
          error?.message || strings.GlobalMessage.Error
        );
      }
    });
  }
}
